import * as React from 'react';
import { action } from 'mobx';
import { RefObject } from 'react';
import update from 'react-addons-update';
import { PageProps, PageToolEvents, RowUpdate } from '../../../../constants';
import { CalendarLayoutDataModel, InfinityRetrieve } from '../../../../models';
import ScheduleModel from './models/ScheduleModel';
import { ScheduleTemplate } from './Schedule.template';
import { PageComponent } from '../../../../utils';
import { TableLayout } from '../../../../components';
import {
  Date6,
  Today,
} from '../../../../utils/time';
import { ConfirmWarning } from '../../../../utils/confirm';
import { CalendarItem } from '../../../../utils/calendar/Calendar';

interface ScheduleState {
  stmon: string;
  perid: string;
  pernm: string;
  actcd: string;
  actnm: string;
  gubun: string;

  schedules: Array<CalendarLayoutDataModel<ScheduleModel>>;
  data: Array<ScheduleModel>;

  isVisibleModal: boolean;
  isVisibleExpire: boolean;
}

/**
 * 컨트롤러
 * @window w_tb_b501
 * @category 영업스케쥴
 */
export class Schedule extends PageComponent<PageProps, ScheduleState>
  implements PageToolEvents {
  infinity?: InfinityRetrieve;

  table: RefObject<TableLayout> = React.createRef();

  rowsUpdated: Array<ScheduleModel> = [];

  tableFocus?: ScheduleModel;

  tableFocusIndex: number = 0;

  tabIndex: number = 0;

  done_cnt: number = 0; // 계획 총합

  plan_cnt: number = 0; // 방문 총합

  total: number = 0; // 총합

  dayRemarks: Record<string, { done: number, plan: number }>;


  constructor(props: PageProps, context: any) {
    super(props, context);
    this.props.onMount && this.props.onMount(this);

    this.dayRemarks = {};

    this.state = props.state || {
      stmon: Date6.make(),
      perid: this.props.publicStore?.user.perid,
      pernm: this.props.publicStore?.user.pernm,
      actcd: '',
      actnm: '',
      gubun: '%',

      schedules: [], // Calendar data
      data: [], // Modal table

      isVisibleModal: false,
    };
  }


  @action
  async onFirstOpenEvent() {
    await this.onRetrieveEvent();
  }

  @action
  async onRetrieveEvent() {
    const { actionStore: api } = this.props;

    await this.SS({
      data: [],
      schedules: [],
    });

    const data = await api.retrieve({
      stmon: this.state.stmon || Date6.make(),
      perid: this.state.perid || '%',
      actcd: this.state.actcd || '%',
      gubun: this.state.gubun || '%',
    });

    this.dayRemarks = {};
    this.done_cnt = 0;
    this.plan_cnt = 0;
    this.total = 0;

    if (data?.items) {
      data?.items?.forEach((x: any) => {
        if (!this.dayRemarks[x.plandate]) {
          this.dayRemarks[x.plandate] = { done: 0, plan: 0 };
        }

        if (x.state === '2') {
          this.dayRemarks[x.plandate].done += 1;
          this.done_cnt += 1;
          this.total += 1;
        } else {
          this.dayRemarks[x.plandate].plan += 1;
          this.plan_cnt += 1;
          this.total += 1;
        }
      });
    }

    await this.SS({
      schedules: data?.items?.map((x: any) => new CalendarLayoutDataModel<ScheduleModel>(x.plandate, x)) || [],
    });
  }

  @action
  async onPrintEvent() {
    if (!ConfirmWarning.assert(this.state.schedules.length > 0, '오류', '출력할 내역이 없습니다.')) {
      return;
    }

    const { actionStore: api } = this.props;
    await api.printWithElmanManager({
      stmon: this.state.stmon || Date6.make(),
      perid: this.state.perid || '%',
      actcd: this.state.actcd || '%',
      gubun: this.state.gubun || '%',
      prtgubun: 'a4',
    });
  }


  // Calendar
  getItemPrefix(item: ScheduleModel): string {
    if (item.state === '1') {
      return '계획';
    }

    return '방문';
  }

  @action
  async onCalendarItemClicked(
    c: CalendarItem,
    _: Array<CalendarLayoutDataModel<ScheduleModel>>,
  ) {
    // 계획 없을때도 신규 등록을 위해 주석처리
    // @author 조태호
    // @date 2021-08-26
    //
    // if (items.length < 1) {
    //   ConfirmWarning.show('오류', '계획된 점검이 없습니다');
    //   return;
    // }

    await this.SS({
      isVisibleModal: true,
    });

    this.tableFocusIndex = 0;
    this.tableFocus = undefined;
    this.rowsUpdated = [];

    const { actionStore: api } = this.props;
    const data = await api.retrieve({
      sub: 'w_popup_b501',
      date: c.data,
    });

    if (data?.items) {
      await this.SS({
        data: data.items.map((x: any) => new ScheduleModel(x)),
      });

      await this.table.current?.update(true);
      this.table.current?.setFocus(0);
    } else {
      await this.SS({
        data: [],
      });
      await this.table.current?.update(true);
    }
  }


  // Modal
  @action
  async onRowFocusEvent(item: ScheduleModel | undefined, index: number) {
    if (!item) {
      this.tableFocus = item;
      this.tableFocusIndex = 0;
      return;
    }

    this.tableFocus = item;
    this.tableFocusIndex = index;
  }

  @action
  closeModal() {
    this.onRetrieveEvent();
    this.SS({ isVisibleModal: false });
  }

  @action
  async onModalPrint() {
    const { actionStore: api } = this.props;

    await api.printWithElmanManager({
      sub: 'w_popup_b501',
      date: this.state.stmon || Date6.make(),
    });
  }

  @action
  async onModalAdd() {
    const { actionStore: api } = this.props;
    const data = await api.new({
      sub: 'w_popup_b501',
      plandate: this.tableFocus?.plandate,
    });

    if (data) {
      const one = new ScheduleModel({
        ...data,
        plandate: Today.date(),
      }, true);

      await this.SS({
        data: [one, ...this.state.data],
      });

      await this.table.current?.update(false);
      this.table.current?.setFocus(0, 2);
    }
  }

  @action
  async onModalRemove() {
    if (!ConfirmWarning.assert(this.tableFocus, '오류', '선택된 항목이 없습니다')) {
      return;
    }

    if (!ConfirmWarning.assert(
      this.tableFocus?.appgubun === '' || this.tableFocus?.appgubun === '131',
      '오류',
      '이미 결재가 상신되었습니다',
    )) {
      return;
    }

    const { actionStore: api } = this.props;
    const text = `${this.tableFocus?.actnm}`;

    if (await api.delete(text, {
      sub: 'w_popup_b501',
      plandate: this.tableFocus?.plandate,
      plannum: this.tableFocus?.plannum,
    })) {
      await this.SS({
        data: update(this.state.data, {
          $splice: [[this.tableFocusIndex, 1]],
        }),
      });

      await this.table.current?.update(false);

      // 위에서 새로고침 없이 행 삭제 후 다음행 포커싱
      if (this.state.data.length) {
        const next = this.tableFocusIndex === 0 ? 0 : this.tableFocusIndex - 1;
        this.table.current?.setFocus(next);
      } else {
        this.tableFocus = undefined;
        this.tableFocusIndex = 0;
      }
    }
  }

  @action
  async onModalSave() {
    const { actionStore: api } = this.props;
    if (await api.save(
      {
        sub: 'w_popup_b501',
        items: this.state.data,
      },
      false,
    )) {
      await this.table.current?.update(true);
      this.table.current?.setFocus(0);
    }
  }

  @action
  onChangeTable(rows: Array<any>, updatedRows: Array<any>) {
    this.rowsUpdated = updatedRows;
    this.setState({
      data: rows,
    });
  }

  @action
  async modalTableItemChanged(rowUpdate: RowUpdate, key: string, value: string) {
    const { actionStore: api } = this.props;
    const result = await api.fxExec('dw_list_itemchanged', {
      ...this.state.data[this.tableFocusIndex],
      [key]: value,
      itemname: key,
      data: value,
      sub: 'w_popup_b501',
    }, this.tableFocus?.isNew);

    if (result) {
      rowUpdate(result);
    }
  }

  getItemColor(item: ScheduleModel): string {
    if (item.state === '1') {
      return 'var(--color-red)';
    }

    return 'var(--color-blue)';
  }


  render() {
    return <ScheduleTemplate
      scope={this}
    />;
  }
}
